// src/pages/LocalNewsViewPage.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation as useRouterLocation } from 'react-router-dom'; 
import ReactDOM from 'react-dom';
import { injectMixedContent } from '../utils/adInjector';
import apiClient from '../utils/api';
import NewsCard from '../components/newsCard';
import AdCard from '../components/AdCard';
import WishesCard from '../components/WishesCard';

// IMPORT LOCATION CONTEXT, AUTH AND CONSTANTS FOR TRANSLATION
import { useLocation as useLocationContext } from '../contexts/LocationContext'; 
import { useAuth } from '../contexts/AuthContext';
import { locationData } from '../utils/constants'; // Official bilingual translations
import { App } from '@capacitor/app';


import './LocalNewsViewPage.css';

// Cleans geocoded strings by scanning locationData for an exact or substring match across all address fields
const getCleanedLocationFromConstants = (locationObj) => {
    const clean = {
        stateEn: '',
        stateTe: '',
        districtEn: '',
        districtTe: '',
        constituencyEn: '',
        constituencyTe: '',
        mandalEn: '',
        mandalTe: ''
    };

    if (!locationObj) return clean;

    const norm = (s) => String(s || '').trim().toLowerCase();
    
    // Gather all string values from the geocoded location object to verify against constants
    const locationValues = Object.values(locationObj)
        .filter(v => typeof v === 'string')
        .map(v => norm(v));

    const rawState = norm(locationObj.state || locationObj.adminArea || '');
    const rawDistrict = norm(locationObj.district || locationObj.subAdminArea || locationObj.city || '');

    // 1. Match State
    const stateMatch = locationData.find(s => {
        const sEn = norm(s.state_en);
        const sTe = norm(s.state_te);
        return sEn === rawState || sTe === rawState || locationValues.some(val => val.includes(sEn) || val.includes(sTe));
    });
    
    if (stateMatch) {
        clean.stateEn = stateMatch.state_en;
        clean.stateTe = stateMatch.state_te;

        // 2. Match District (Handles "Kurnool District" or similar raw address strings)
        if (rawDistrict || locationValues.length > 0) {
            const districtMatch = stateMatch.districts?.find(d => {
                const dEn = norm(d.district_en);
                const dTe = norm(d.district_te);
                return (
                    dEn === rawDistrict || 
                    dTe === rawDistrict || 
                    locationValues.some(val => val.includes(dEn) || val.includes(dTe))
                );
            });
            if (districtMatch) {
                clean.districtEn = districtMatch.district_en;
                clean.districtTe = districtMatch.district_te;

                // 3. Match Constituency
                const constituencyMatch = districtMatch.constituencies?.find(c => {
                    const cVal = norm(c.value);
                    const cLab = norm(c.label);
                    return locationValues.some(val => val.includes(cVal) || val.includes(cLab));
                });
                if (constituencyMatch) {
                    clean.constituencyEn = constituencyMatch.value;
                    clean.constituencyTe = constituencyMatch.label;
                }

                // 4. Match Mandal (Handles "Kallur Mandal" or similar address strings)
                const mandalMatch = districtMatch.mandals?.find(m => {
                    const mVal = norm(m.value);
                    const mLab = norm(m.label);
                    return locationValues.some(val => val.includes(mVal) || val.includes(mLab));
                });
                if (mandalMatch) {
                    clean.mandalEn = mandalMatch.value;
                    clean.mandalTe = mandalMatch.label;
                }
            }
        }
    }

    return clean;
};

const LocalNewsViewPage = ({ setActiveCommentPostId }) => {
    const navigate = useNavigate();
    const routerLocation = useRouterLocation();
    const [searchParams] = useSearchParams();
    
    // Extract authenticated profile details and GPS Context values
    const { currentUser } = useAuth();
    const { locationCoords, locationName, isDetecting, detectAndSaveLocation, locationError } = useLocationContext();

    const [mixedContent, setMixedContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [hasLocalNews, setHasLocalNews] = useState(true); 

    const lat = searchParams.get('lat');
    const lon = searchParams.get('lon');
    const city = searchParams.get('city');

    const [fullImageUrl, setFullImageUrl] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const activeIndexRef = useRef(0);
    const animatingRef = useRef(false);
    const stageRef = useRef(null);
    const cardRefsMap = useRef({});
    const mixedContentRef = useRef([]);

    // Automatically trigger GPS detection on mount if coordinates are missing
    useEffect(() => {
        if (!lat && !lon && !locationCoords?.latitude && !isDetecting) {
            detectAndSaveLocation().catch(err => console.warn("Auto-detect failed:", err));
        }
    }, [lat, lon, locationCoords, isDetecting, detectAndSaveLocation]);

    // DYNAMIC COORDINATES FALLBACK: Use URL parameters if present, otherwise fall back to context coordinates
    const activeLat = lat || locationCoords?.latitude;
    const activeLon = lon || locationCoords?.longitude;
    const activeCity = city || locationName?.city || 'Your Area';

    useEffect(() => {
        if (!window.Capacitor) return;
        
        const listener = App.addListener('backButton', () => {
            if (routerLocation.pathname.startsWith('/posts/LocalNews')) {
                if (window.history.length > 1) {
                    navigate(-1); 
                } else {
                    navigate('/', { replace: true });
                }
            } 
        });
        
        return () => {
            listener.then(l => {
             if (l && typeof l.remove === 'function') {
                 l.remove();
             }
            }).catch(err => console.warn("Failed to remove backButton listener:", err));
        };
    }, [navigate, routerLocation.pathname]);

    // Main Fetch Logic
    useEffect(() => {
        const fetchNews = async () => {
            if (!activeLat || !activeLon) {
                if (isDetecting) {
                    setLoading(true);
                    setFetchError(null);
                }
                return;
            }
            
            setLoading(true);
            setFetchError(null);

            try {
                // 1. Retrieve the profile location from the authenticated user first (highly accurate, validated keys)
                const profileLocation = currentUser?.profile?.location || {};
                
                const geocodedState = profileLocation.state || locationName?.state || 'Andhra Pradesh';
                const geocodedDistrict = profileLocation.district || locationName?.district || '';
                const geocodedConstituency = profileLocation.constituency || locationName?.constituency || '';
                const geocodedMandal = profileLocation.mandal || locationName?.mandal || '';

                // 2. Clean the raw GPS inputs using our locationData matching constants
                const cleanLoc = getCleanedLocationFromConstants({
                    state: geocodedState,
                    district: geocodedDistrict,
                    constituency: geocodedConstituency,
                    mandal: geocodedMandal,
                    city: activeCity
                });

                // Fallbacks to cleaned data keys matching database fields
                const activeStateEn = cleanLoc.stateEn || geocodedState;
                const activeDistrictEn = cleanLoc.districtEn || geocodedDistrict;
                const activeDistrictTe = cleanLoc.districtTe || '';
                const activeConstituencyEn = cleanLoc.constituencyEn || geocodedConstituency;
                const activeMandalEn = cleanLoc.mandalEn || geocodedMandal;

                // 3. Fetch news streams concurrently with higher limits using the cleaned exact database keys
                const [localRes, textDistrictEnRes, textDistrictTeRes, stateRes, adsRes] = await Promise.all([
                    // Coords query
                    apiClient.get('/user-news/local', { params: { latitude: activeLat, longitude: activeLon } }).catch(() => ({ data: [] })),
                    // English District Query (e.g. district = "Kurnool")
                    activeDistrictEn ? apiClient.get('/user-news', { params: { district: activeDistrictEn, limit: 45 } }).catch(() => ({ data: [] })) : { data: [] },
                    // Telugu District Query (e.g. district = "కర్నూలు")
                    activeDistrictTe ? apiClient.get('/user-news', { params: { district: activeDistrictTe, limit: 45 } }).catch(() => ({ data: [] })) : { data: [] },
                    // State fallback query stream
                    apiClient.get('/user-news', { params: { postType: activeStateEn, limit: 40 } }).catch(() => ({ data: [] })),
                    // Ads query
                    apiClient.get('/ads').catch(() => ({ data: [] }))
                ]);

                // Collect local items from coords and district text matches
                const rawLocalItems = [
                    ...(localRes.data.news || localRes.data || []),
                    ...(textDistrictEnRes.data || []),
                    ...(textDistrictTeRes.data || [])
                ];
                const rawStateItems = stateRes.data || [];

                // Deduplicate items globally
                const allUniqueRawItems = [];
                const seenIds = new Set();

                const addUniqueItems = (items, defaultFlags = {}) => {
                    (items || []).forEach(item => {
                        if (item && item.id && !seenIds.has(item.id)) {
                            seenIds.add(item.id);
                            allUniqueRawItems.push({ ...item, ...defaultFlags });
                        }
                    });
                };

                addUniqueItems(rawLocalItems, { isLocal: true });
                addUniqueItems(rawStateItems, { isState: true });

                const normalize = (str) => String(str || '').trim().toLowerCase();

                const isLocalMatch = (item) => {
                    if (item.isLocal) return true;
                    
                    const itemDistrict = normalize(item.district);
                    const itemConstituency = normalize(item.constituency);
                    const itemMandal = normalize(item.mandal);
                    const itemTitle = normalize(item.title);
                    const itemContent = normalize(item.content || item.description);
                    
                    const districtTargets = [normalize(activeDistrictEn), normalize(activeDistrictTe)].filter(Boolean);
                    const constituencyTargets = [normalize(activeConstituencyEn), normalize(cleanLoc.constituencyTe)].filter(Boolean);
                    const mandalTargets = [normalize(activeMandalEn), normalize(cleanLoc.mandalTe), 'kallur', 'కల్లూరు'].filter(Boolean);

                    // Strict matching
                    if (itemDistrict && districtTargets.length > 0) {
                        if (districtTargets.some(target => itemDistrict === target || itemDistrict.includes(target) || target.includes(itemDistrict))) {
                            return true;
                        }
                    }
                    if (itemConstituency && constituencyTargets.length > 0) {
                        if (constituencyTargets.some(target => itemConstituency === target || itemConstituency.includes(target))) {
                            return true;
                        }
                    }
                    if (itemMandal && mandalTargets.length > 0) {
                        if (mandalTargets.some(target => itemMandal === target || itemMandal.includes(target))) {
                            return true;
                        }
                    }
                    
                    // Fallbacks for textual searches
                    if (activeDistrictTe && (itemTitle.includes(normalize(activeDistrictTe)) || itemContent.includes(normalize(activeDistrictTe)))) {
                        return true;
                    }
                    if (activeDistrictEn && (itemTitle.includes(normalize(activeDistrictEn)) || itemContent.includes(normalize(activeDistrictEn)))) {
                        return true;
                    }
                    if (cleanLoc.mandalTe && (itemTitle.includes(normalize(cleanLoc.mandalTe)) || itemContent.includes(normalize(cleanLoc.mandalTe)))) {
                        return true;
                    }
                    
                    return false;
                };

                const tierLocal = [];
                const tierState = [];

                allUniqueRawItems.forEach(item => {
                    if (isLocalMatch(item)) {
                        tierLocal.push(item);
                    } else if (item.isState || item.postType === activeStateEn) {
                        tierState.push(item);
                    }
                });

                // Chronological sorting function
                const sortByTimestamp = (arr) => {
                    return arr.sort((a, b) => {
                        const tA = a.timestamp?._seconds ? a.timestamp._seconds * 1000 : new Date(a.timestamp || 0).getTime();
                        const tB = b.timestamp?._seconds ? b.timestamp._seconds * 1000 : new Date(b.timestamp || 0).getTime();
                        return tB - tA;
                    });
                };

                const sortedLocal = sortByTimestamp(tierLocal);
                const sortedState = sortByTimestamp(tierState);

                let allNewsCombined = [];

                // 💡 SEQUENTIAL FEED: District news is placed first at the top. State news follows directly.
                if (sortedLocal.length > 0) {
                    setHasLocalNews(true);
                    allNewsCombined.push(...sortedLocal);
                } else {
                    setHasLocalNews(false);
                    // Show a prompt card at the top if there is zero local news in your district
                    allNewsCombined.push({
                        id: 'prompt-card',
                        isPrompt: true,
                        title: "మీ ప్రాంతపు వార్తలు ఇంకా అందుబాటులో లేవు",
                        content: "ジャーナリストగా మారి మీ ప్రాంతపు వార్తలను మొట్టమొదటిగా పోస్ట్ చేయండి! మీ చుట్టూ జరుగుతున్న విశేషాలను అందరికీ తెలియజేయండి.",
                        imageUrl: "https://firebasestorage.googleapis.com/v0/b/inewsbook-1402f.appspot.com/o/static%2Fjournalist_placeholder.png?alt=media",
                        source: "Ai News",
                        category: "Update"
                    });
                }

                // Append State News directly underneath
                allNewsCombined.push(...sortedState);

                const processedNews = allNewsCombined.map(item => ({
                    ...item,
                    description: item.content || item.description || "", 
                    timestamp: item.timestamp?._seconds ? new Date(item.timestamp._seconds * 1000) : new Date(item.timestamp || Date.now())
                }));

                setMixedContent(injectMixedContent(processedNews, adsRes.data || [], 4));

            } catch (error) {
                console.error("Critical Fetch Error:", error);
                setFetchError("వార్తలను లోడ్ చేయడంలో విఫలమైంది.");
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, [activeLat, activeLon, isDetecting, locationName, activeCity, navigate, currentUser]);

    const applyStyles = useCallback((index, progress) => {
    const items = mixedContentRef.current;
    items.forEach((item, i) => {
        const key = item.id || item.adId || `slide-${i}`;
        const el = cardRefsMap.current[key];
        if (!el) return;
        const off = i - index;
        if (Math.abs(off) > 1) { el.style.display = 'none'; return; }
        el.style.display = 'block';
        if (off === 0) {
            const deg = progress * -90;
            const tz = Math.abs(progress) * -60;
            el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
            el.style.opacity = String(1 - Math.abs(progress) * 0.5);
            el.style.zIndex = '10';
        } else {
            const deg = 90 + progress * -90;
            const tz = Math.abs(progress) * -60;
            el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
            el.style.opacity = String(Math.abs(progress) * 0.9);
            el.style.zIndex = '5';
        }
    });
}, []);

const goNext = useCallback(() => {
    if (animatingRef.current) return;
    if (activeIndexRef.current >= mixedContentRef.current.length - 1) return;
    animatingRef.current = true;
    Object.values(cardRefsMap.current).forEach(el => {
        if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
    });
    applyStyles(activeIndexRef.current, -1);
    setTimeout(() => {
        activeIndexRef.current += 1;
        setActiveIndex(activeIndexRef.current);
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'none';
        });
        applyStyles(activeIndexRef.current, 0);
        animatingRef.current = false;
    }, 380);
}, [applyStyles]);

const goPrev = useCallback(() => {
    if (animatingRef.current) return;
    if (activeIndexRef.current <= 0) return;
    animatingRef.current = true;
    Object.values(cardRefsMap.current).forEach(el => {
        if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
    });
    activeIndexRef.current -= 1;
    setActiveIndex(activeIndexRef.current);
    applyStyles(activeIndexRef.current, 0);
    setTimeout(() => { animatingRef.current = false; }, 380);
}, [applyStyles]);

useEffect(() => {
    mixedContentRef.current = mixedContent;
    applyStyles(activeIndexRef.current, 0);
}, [mixedContent, applyStyles]);

useEffect(() => {
    // Poll until stageRef is available (fires after loading screen disappears)
    const stage = stageRef.current;
    if (!stage) return;

    let startY = 0, lastDelta = 0;
    let scrollableTarget = null;

    // Traverses up from the touch target to find any parent element configured to scroll
    const findScrollableParent = (el, stopEl) => {
        let current = el;
        while (current && current !== stopEl) {
            if (current.scrollHeight > current.clientHeight) {
                const style = window.getComputedStyle(current);
                const overflowY = style.overflowY || style.overflow || '';
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    return current;
                }
            }
            current = current.parentElement;
        }
        return null;
    };
    const onTouchStart = (e) => {
        startY = e.touches[0].clientY;
        lastDelta = 0;
        // Find if touch sequence starts inside a scrollable news description/container
        scrollableTarget = findScrollableParent(e.target, stage);
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'none';
        });
    };
    const onTouchMove = (e) => {
        const dy = e.touches[0].clientY - startY;

        if (scrollableTarget) {
            const scrollTop = scrollableTarget.scrollTop;
            const maxScroll = scrollableTarget.scrollHeight - scrollableTarget.clientHeight;

            // Dragging UP (scrolling text DOWN) -> Allow native scroll if not at the absolute bottom
            if (dy < 0 && scrollTop < maxScroll) {
                lastDelta = 0; // Reset swipe delta to prevent accidental page transitions
                return;
            }
            // Dragging DOWN (scrolling text UP) -> Allow native scroll if not at the absolute top
            if (dy > 0 && scrollTop > 0) {
                lastDelta = 0; // Reset swipe delta to prevent accidental page transitions
                return;
            }
        }

        // If not in a scrollable block, or we have hit the boundaries, execute the card swipe transition
        if (e.cancelable) e.preventDefault();
        lastDelta = dy;
        const progress = Math.max(-1, Math.min(1, dy / stage.clientHeight));
        applyStyles(activeIndexRef.current, -progress);
    };
    const onTouchEnd = () => {
        if (lastDelta < -60) goNext();
        else if (lastDelta > 60) goPrev();
        else {
            Object.values(cardRefsMap.current).forEach(el => {
                if (el) el.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
            });
            applyStyles(activeIndexRef.current, 0);
        }
        scrollableTarget = null;
    };
    const onWheel = (e) => {
        e.preventDefault();
        if (e.deltaY > 25) goNext();
        else if (e.deltaY < -25) goPrev();
    };

    stage.addEventListener('touchstart', onTouchStart, { passive: false });
    stage.addEventListener('touchmove', onTouchMove, { passive: false });
    stage.addEventListener('touchend', onTouchEnd, { passive: false });
    stage.addEventListener('wheel', onWheel, { passive: false });

    return () => {
        stage.removeEventListener('touchstart', onTouchStart);
        stage.removeEventListener('touchmove', onTouchMove);
        stage.removeEventListener('touchend', onTouchEnd);
        stage.removeEventListener('wheel', onWheel);
    };
// ADD loading and mixedContent to deps so it re-runs after loading finishes
}, [applyStyles, goNext, goPrev, loading, mixedContent]);

    if (isDetecting && !activeLat) {
        return (
            <div className="fullscreen-loader">
                <div className="spinner-glow"></div>
                <span className="loading-text-spinner">Detecting your location (స్థానాన్ని గుర్తిస్తోంది)...</span>
            </div>
        );
    }

    if (!isDetecting && (!activeLat || !activeLon)) {
        return (
            <div className="no-data-found-fullscreen" style={{ padding: '20px' }}>
                <i className="fas fa-map-marker-alt" style={{ fontSize: '4rem', color: '#ff9800', marginBottom: '20px' }}></i>
                <h3>స్థానాన్ని గుర్తించలేదు</h3>
                <p>స్థానిక వార్తలను చూడటానికి దయచేసి మీ స్థానాన్ని అనుమతించండి.</p>
                <button onClick={detectAndSaveLocation} className="button-primary" style={{ padding: '12px 24px', fontSize: '16px' }}>
                    Detect Location (స్థానాన్ని గుర్తించండి)
                </button>
                {locationError && <div className="loc-error-small" style={{ marginTop: '15px', color: 'red' }}>{locationError}</div>}
            </div>
        );
    }

    if (loading) return <div className="fullscreen-loader">వార్తలను లోడ్ చేస్తోంది...</div>;
    if (fetchError) return <div className="fullscreen-loader error">{fetchError}</div>;

    return (
        <div className="local-view-container">
            {/* Overlay badge positioned at the top-right */}
            <div className="local-area-badge">
                📍 {activeCity}
            </div>
            
            <div
    className="cube-stage"
    ref={stageRef}
    style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
>
    {fullImageUrl && ReactDOM.createPortal(
        <div
            onClick={() => setFullImageUrl(null)}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.96)',
                zIndex: 999999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <img
                src={fullImageUrl}
                alt="Full view"
                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
                onClick={e => e.stopPropagation()}
            />
            <span
                onClick={() => setFullImageUrl(null)}
                style={{
                    position: 'absolute', top: 40, right: 20,
                    color: '#fff', fontSize: 32,
                    cursor: 'pointer', padding: '8px 12px',
                }}
            >✕</span>
        </div>,
        document.body
    )}
    {mixedContent.map((item, index) => {
    const key = item.id || item.adId || `slide-${index}`;
        const isActive = index === activeIndex;
    // 💡 BULLETPROOF DETECTOR: Handles both raw strings and string arrays
    const isWishesCard = (() => {
        const checkMatch = (val) => {
            if (!val) return false;
            const checkStr = (str) => {
                const s = String(str).toLowerCase();
                return s.includes('wish') || s.includes('special') || s.includes('శుభాకాంక్షలు') || s.includes('పుట్టినరోజు');
            };
            if (Array.isArray(val)) {
                return val.some(item => checkStr(item));
            }
            return checkStr(val);
        };

        const hasExactMatch = (val) => {
            if (!val) return false;
            const targetTypes = ['special day wishes', 'specialdaywishes', 'wishes', 'ప్రత్యేక శుభాకాంక్షలు'];
            if (Array.isArray(val)) {
                return val.some(item => targetTypes.includes(String(item).toLowerCase()));
            }
            return targetTypes.includes(String(val).toLowerCase());
        };

        return (
            hasExactMatch(item.postType) ||
            checkMatch(item.postType) ||
            checkMatch(item.category) ||
            checkMatch(item.title)
        );
    })();

    return (
        <div
                key={key}
                className="cube-slide"
                ref={el => {
                    if (el) cardRefsMap.current[key] = el;
                    else delete cardRefsMap.current[key];
                }}
                style={{
                    position: 'absolute', top: 0, left: 0,
                    width: '100%', height: '100%',
                    display: 'none', willChange: 'transform',
                }}
            >
                {item.isAd ? (
                    <AdCard adData={item} isActive={isActive} />
                ) : item.isPrompt ? (
                    <div className="news-card prompt-card">
                        <img src={item.imageUrl} alt="prompt" className="news-card-image" />
                        <div className="news-card-content">
                            <h3 className="news-card-title">{item.title}</h3>
                            <p className="news-card-description">{item.content}</p>
                            <button className="post-now-btn" onClick={() => navigate('/post-ad')}>
                                పోస్ట్ చేయండి +
                            </button>
                        </div>
                    </div>
                ) : isWishesCard ? (
                    <WishesCard
                        newsItem={{ ...item, description: item.content || item.description }}
                        isActive={isActive}
                        setActiveCommentPostId={setActiveCommentPostId}
                    />
                ) : (
                    <NewsCard
                        newsItem={item}
                        isFeedView={true}
                        setActiveCommentPostId={setActiveCommentPostId}
                        isActive={isActive}
                        onImageOpen={(url) => setFullImageUrl(url)}
                    />
                )}
            </div>
        );
    })}
</div>
        </div>
    );
};

export default LocalNewsViewPage;